import React, { useState, useRef, useEffect } from 'react';
import { CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { CheckCircle, XCircle, Palette, BarChart3, Sparkles, Play, Image, Maximize2, X, Calendar, Share2 } from 'lucide-react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Custom formatter to display dollar values with commas
const formatDollar = (value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

// Format percentage values
const formatPercent = (value) => {
  if (typeof value !== 'number' || isNaN(value)) {
    return 'N/A';
  }
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};



const CreativeReport = () => {
  const navigate = useNavigate();
  const { getAccountId, getAccountDisplayName } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingTimer, setLoadingTimer] = useState(null);
  const [selectedRange, setSelectedRange] = useState(7); // Match dashboard default
  const [dateRangeType, setDateRangeType] = useState('preset'); // 'preset' or 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [visualSubTab, setVisualSubTab] = useState('adTypes');
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'roas', direction: 'desc' });
  const [topPerformersSortConfig, setTopPerformersSortConfig] = useState({ key: 'roas', direction: 'desc' });
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  // Handle Escape key to close fullscreen modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && fullscreenMedia) {
        setFullscreenMedia(null);
      }
    };

    if (fullscreenMedia) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [fullscreenMedia]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (loadingTimer) {
        clearInterval(loadingTimer);
      }
    };
  }, [loadingTimer]);

  // Preset date ranges (enhanced with longer periods)
  const getPresetRanges = () => [
    { label: 'Last 7 days', days: 7, color: 'from-blue-500 to-cyan-500' },
    { label: 'Last 30 days', days: 30, color: 'from-purple-500 to-pink-500' },
    { label: 'Last 90 days', days: 90, color: 'from-green-500 to-emerald-500' },
    { label: 'Last 365 days', days: 365, color: 'from-orange-500 to-red-500' },
    { label: 'Last 2 years', days: 730, color: 'from-indigo-500 to-purple-500' }
  ];

  // Handle preset range selection
  const handlePresetRange = (days) => {
    setSelectedRange(days);
    setDateRangeType('preset');
    // Clear any existing report data to force regeneration
    setReportData(null);
  };

  // Handle custom date range selection
  const handleCustomDateRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setDateRangeType('custom');
    // Clear any existing report data to force regeneration
    setReportData(null);
  };

  // Handle sharing the report
  const handleShareReport = async () => {
    try {
      setIsSharing(true);
      const accountId = getAccountId();
      if (!accountId || !reportData) {
        toast.error('Unable to share report. Please try again.');
        return;
      }

      const dateRange = formatDateRange();
      
      const response = await axios.post('/api/reports/share', {
        accountId,
        dateRange,
        reportData,
        aiInsights
      });

      if (response.data.success) {
        const shareUrl = response.data.shareUrl;
        
        // Copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        
        toast.success('Report shared! Link copied to clipboard.');
        
        // Show share modal with link
        setShareModalOpen(true);
        setShareUrl(shareUrl);
      } else {
        toast.error('Failed to share report. Please try again.');
      }
    } catch (error) {
      console.error('Error sharing report:', error);
      toast.error('Failed to share report. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  // Format date range display
  const formatDateRange = () => {
    if (dateRangeType === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return `Last ${selectedRange} days`;
  };

  // Get SQL date range string
  const getSQLDateRange = () => {
    if (dateRangeType === 'custom' && startDate && endDate) {
      return `date >= '${startDate}' AND date <= '${endDate}'`;
    }
    return `date >= CURRENT_DATE - INTERVAL '${selectedRange} days'`;
  };



  // Media preview component with fullscreen support
  const MediaPreview = ({ url, title, isVideo = false }) => {
    const handleClick = () => {
      console.log('MediaPreview clicked:', { url, title, isVideo });
      console.log('Setting fullscreen media:', { url, title, type: isVideo ? 'video' : 'image' });
      setFullscreenMedia({ url, title, type: isVideo ? 'video' : 'image' });
    };

    return (
      <div className="relative group">
        <div 
          className="w-24 h-20 sm:w-32 sm:h-24 lg:w-40 lg:h-32 bg-gray-200 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
          onClick={handleClick}
        >
          {isVideo ? (
            <video 
              src={url} 
              className="w-full h-full object-cover"
              controls
              preload="metadata"
              onError={(e) => {
                console.error('Video failed to load:', url);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <img 
              src={url} 
              alt={title}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Image failed to load:', url);
                e.target.style.display = 'none';
              }}
            />
          )}
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {isVideo ? <Play className="h-8 w-8 text-white" /> : <Maximize2 className="h-8 w-8 text-white" />}
          </div>
        </div>
      </div>
    );
  };

  // Render feature examples with images/videos
  const renderFeatureExamples = (featureName, item = null, isPositive = true) => {
    // If we have an item with asset URLs, use those
    if (item && item.assetUrls && item.assetUrls.length > 0) {
      const assetUrls = Array.isArray(item.assetUrls) ? item.assetUrls : [item.assetUrls];
      const mediaTypes = item.mediaTypes || [];
      
      // Filter out empty or null URLs - more robust filtering
      const validAssetUrls = assetUrls.filter(url => {
        if (!url) return false;
        if (typeof url !== 'string') return false;
        if (url.trim() === '') return false;
        if (url === 'null' || url === 'undefined') return false;
        return true;
      });
      
      console.log('URL filtering:', {
        originalUrls: assetUrls,
        validUrls: validAssetUrls,
        featureName: featureName,
        originalLength: assetUrls.length,
        validLength: validAssetUrls.length
      });
      
      if (validAssetUrls.length === 0) {
        return null; // No valid URLs to display
      }
      
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {validAssetUrls.slice(0, 2).map((url, index) => {
            // Better media type detection based on URL path and extension (prioritize URL over database)
            const urlLower = url.toLowerCase();
            const isVideoFromUrl = urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.avi') || urlLower.includes('.webm') || urlLower.includes('/video/');
            const isImageFromUrl = urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif') || urlLower.includes('.webp') || urlLower.includes('/image/');
            
            // Prioritize URL-based detection over database media type for accuracy
            let shouldRenderAsVideo = false;
            if (isVideoFromUrl) {
              shouldRenderAsVideo = true;
            } else if (isImageFromUrl) {
              shouldRenderAsVideo = false;
            } else {
              // Fallback to database media type only if URL doesn't have clear extension
              const dbMediaType = mediaTypes[index];
              shouldRenderAsVideo = dbMediaType === 'Video';
            }
            
            console.log('Media type detection:', {
              url,
              index,
              isVideoFromUrl,
              isImageFromUrl,
              dbMediaType: mediaTypes[index],
              shouldRenderAsVideo,
              featureName
            });
            
            return (
              <div key={index} className="relative">
                <MediaPreview 
                  url={url} 
                  title={`${featureName} example ${index + 1}`}
                  isVideo={shouldRenderAsVideo}
                />
                <div className="absolute -bottom-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {featureName} {isPositive ? '(Top Performer)' : '(Underperformer)'}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // If we have a single asset URL (for top content)
    if (item && item.assetUrl && typeof item.assetUrl === 'string' && item.assetUrl.trim() !== '' && item.assetUrl !== 'null' && item.assetUrl !== 'undefined') {
      const urlLower = item.assetUrl.toLowerCase();
      const isVideoFromUrl = urlLower.includes('.mp4') || urlLower.includes('.mov') || urlLower.includes('.avi') || urlLower.includes('.webm') || urlLower.includes('/video/');
      const isImageFromUrl = urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif') || urlLower.includes('.webp') || urlLower.includes('/image/');
      
      // Prioritize URL-based detection over database media type for accuracy
      let shouldRenderAsVideo = false;
      if (isVideoFromUrl) {
        shouldRenderAsVideo = true;
      } else if (isImageFromUrl) {
        shouldRenderAsVideo = false;
      } else {
        // Fallback to database media type only if URL doesn't have clear extension
        shouldRenderAsVideo = item.mediaType === 'Video';
      }
      
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          <div className="relative">
            <MediaPreview 
              url={item.assetUrl} 
              title={item.name}
              isVideo={shouldRenderAsVideo}
            />
            <div className="absolute -bottom-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {item.name}
            </div>
          </div>
        </div>
      );
    }

    // No media available
    return null;
  };

  // Dashboard info state
  const [dashboardInfo, setDashboardInfo] = useState({
    title: "Creative Performance Analysis",
    lastUpdated: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    totalSpend: 0,
    averageROAS: 0,
    analyzedCreatives: 0
  });

  // AI insights state
  const [aiInsights, setAiInsights] = useState({
    keyActionItems: '',
    topPerformerInsights: '',
    keyRecommendations: ''
  });
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);

  // Generate AI insights using LLM
  const generateAIInsights = async (data) => {
    console.log('generateAIInsights called with data:', data);
    setIsGeneratingInsights(true);
    try {
      // Prepare data for LLM analysis
      const topAdTypes = data.bestAdTypes.slice(0, 3);
      const topFeatures = data.bestFeatures.slice(0, 5);
      const worstAdTypes = data.worstAdTypes.slice(0, 3);
      const worstFeatures = data.worstFeatures.slice(0, 5);
      
      const analysisData = {
        timeRange: `${selectedRange} days`,
        totalSpend: data.metrics.totalSpend,
        averageROAS: data.metrics.avgROAS,
        topPerformers: {
          adTypes: topAdTypes.map(item => ({
            name: item.name,
            roas: item.roas,
            spend: item.spend,
            impact: item.impact,
            descriptions: item.descriptions?.slice(0, 3) || []
          })),
          features: topFeatures.map(item => ({
            name: item.name,
            roas: item.roas,
            spend: item.spend,
            impact: item.impact,
            descriptions: item.descriptions?.slice(0, 3) || []
          }))
        },
        underperformers: {
          adTypes: worstAdTypes.map(item => ({
            name: item.name,
            roas: item.roas,
            spend: item.spend,
            impact: item.impact,
            descriptions: item.descriptions?.slice(0, 3) || []
          })),
          features: worstFeatures.map(item => ({
            name: item.name,
            roas: item.roas,
            spend: item.spend,
            impact: item.impact,
            descriptions: item.descriptions?.slice(0, 3) || []
          }))
        }
      };

      // Generate insights using chat API with JSON format
      const insightsResponse = await axios.post('/api/chat/message', {
        message: `As a marketing analytics expert, please provide strategic insights and recommendations based on this performance data. This is a general analysis question that does not require database queries. Please respond in valid JSON format only.

Data Summary:
- Time Period: Last ${analysisData.timeRange}
- Total Spend: $${analysisData.totalSpend.toLocaleString()}
- Average ROAS: ${analysisData.averageROAS.toFixed(2)}x

Top Performing Ad Types:
${analysisData.topPerformers.adTypes.map(item => 
  `- ${item.name}: ROAS ${item.roas.toFixed(2)}x, Impact +${item.impact.toFixed(1)}%, Spend $${item.spend.toLocaleString()}
   Sample descriptions: ${item.descriptions.join('; ')}`
).join('\n')}

Top Performing Features:
${analysisData.topPerformers.features.map(item => 
  `- ${item.name}: ROAS ${item.roas.toFixed(2)}x, Impact +${item.impact.toFixed(1)}%, Spend $${item.spend.toLocaleString()}
   Sample descriptions: ${item.descriptions.join('; ')}`
).join('\n')}

Underperforming Ad Types:
${analysisData.underperformers.adTypes.map(item => 
  `- ${item.name}: ROAS ${item.roas.toFixed(2)}x, Impact ${item.impact.toFixed(1)}%, Spend $${item.spend.toLocaleString()}
   Sample descriptions: ${item.descriptions.join('; ')}`
).join('\n')}

Underperforming Features:
${analysisData.underperformers.features.map(item => 
  `- ${item.name}: ROAS ${item.roas.toFixed(2)}x, Impact ${item.impact.toFixed(1)}%, Spend $${item.spend.toLocaleString()}
   Sample descriptions: ${item.descriptions.join('; ')}`
).join('\n')}

Based on this performance data, provide marketing strategy insights and best practices recommendations in this exact JSON format:

{
  "keyActionItems": "2-3 specific actionable recommendations based on the data analysis",
  "topPerformerInsights": "Detailed analysis of what's working and why, based on top performing elements and their descriptions",
  "keyRecommendations": "Strategic recommendations for optimization based on performance patterns"
}

This is a conceptual analysis request about marketing best practices and optimization strategies. Respond with ONLY the JSON object, no other text.`,
        accountId: getAccountId()
      });

      console.log('AI Insights Response:', insightsResponse.data);
      console.log('Response success:', insightsResponse.data.success);
      console.log('Response content:', insightsResponse.data.content);
      console.log('Response explanation:', insightsResponse.data.explanation);
      
      if (insightsResponse.data.success && (insightsResponse.data.content || insightsResponse.data.explanation)) {
        const insights = insightsResponse.data.content || insightsResponse.data.explanation;
        console.log('Using insights from:', insightsResponse.data.content ? 'content' : 'explanation');
        console.log('Raw AI Insights:', insights);
        console.log('Insights length:', insights.length);
        
        try {
          // Try to parse as JSON first
          let parsedInsights;
          
          // Clean the response to extract JSON if wrapped in text
          let jsonContent = insights.trim();
          
          // Look for JSON object in the response
          const jsonMatch = jsonContent.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            jsonContent = jsonMatch[0];
          }
          
          parsedInsights = JSON.parse(jsonContent);
          
          console.log('Parsed JSON insights:', parsedInsights);
          
          // Handle different response structures
          let keyActionItems = '';
          let topPerformerInsights = '';
          let keyRecommendations = '';
          
          if (Array.isArray(parsedInsights.keyActionItems)) {
            keyActionItems = parsedInsights.keyActionItems.join('\n• ');
            if (keyActionItems) keyActionItems = '• ' + keyActionItems;
          } else {
            keyActionItems = parsedInsights.keyActionItems || '';
          }
          
          if (typeof parsedInsights.topPerformerInsights === 'object') {
            const insights = parsedInsights.topPerformerInsights;
            topPerformerInsights = Object.entries(insights)
              .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${value}`)
              .join('\n\n');
          } else {
            topPerformerInsights = parsedInsights.topPerformerInsights || '';
          }
          
          if (typeof parsedInsights.keyRecommendations === 'object') {
            const recommendations = parsedInsights.keyRecommendations;
            keyRecommendations = Object.entries(recommendations)
              .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: ${value}`)
              .join('\n\n');
          } else {
            keyRecommendations = parsedInsights.keyRecommendations || '';
          }
          
          console.log('Processed insights:', { keyActionItems, topPerformerInsights, keyRecommendations });
          
          setAiInsights({
            keyActionItems: keyActionItems || 'Generate insights by analyzing your top and bottom performing elements.',
            topPerformerInsights: topPerformerInsights || 'Insights will be generated based on your performance data.',
            keyRecommendations: keyRecommendations || 'Strategic recommendations will be provided based on your analysis.'
          });
          
        } catch (jsonError) {
          console.log('JSON parsing failed, trying text parsing:', jsonError);
          
          // Fallback to text parsing if JSON fails
          const actionMatch = insights.match(/(?:keyActionItems|KEY ACTION ITEMS|1\.)(.*?)(?=(?:topPerformerInsights|TOP PERFORMER INSIGHTS|2\.|$))/is);
          const insightsMatch = insights.match(/(?:topPerformerInsights|TOP PERFORMER INSIGHTS|2\.)(.*?)(?=(?:keyRecommendations|KEY RECOMMENDATIONS|3\.|$))/is);
          const recommendationsMatch = insights.match(/(?:keyRecommendations|KEY RECOMMENDATIONS|3\.)(.*?)$/is);
          
          const keyActionItems = actionMatch ? actionMatch[1].trim().replace(/^[:\-\s]+/, '') : '';
          const topPerformerInsights = insightsMatch ? insightsMatch[1].trim().replace(/^[:\-\s]+/, '') : '';
          const keyRecommendations = recommendationsMatch ? recommendationsMatch[1].trim().replace(/^[:\-\s]+/, '') : '';
          
          // Final fallback: use the whole response
          if (!keyActionItems && !topPerformerInsights && !keyRecommendations) {
            setAiInsights({
              keyActionItems: insights,
              topPerformerInsights: insights,
              keyRecommendations: insights
            });
          } else {
            setAiInsights({
              keyActionItems: keyActionItems || 'Generate insights by analyzing your top and bottom performing elements.',
              topPerformerInsights: topPerformerInsights || 'Insights will be generated based on your performance data.',
              keyRecommendations: keyRecommendations || 'Strategic recommendations will be provided based on your analysis.'
            });
          }
        }
      } else {
        console.log('AI Insights failed - no success or content:', insightsResponse.data);
        console.log('Setting fallback insights due to API failure');
        setAiInsights({
          keyActionItems: 'Unable to generate AI insights at this time. Please check the console for error details.',
          topPerformerInsights: 'Unable to generate AI insights at this time. Please check the console for error details.',
          keyRecommendations: 'Unable to generate AI insights at this time. Please check the console for error details.'
        });
      }
    } catch (error) {
      console.error('Error generating AI insights:', error);
      // Set fallback insights
      setAiInsights({
        keyActionItems: 'Unable to generate AI insights at this time. Please try regenerating the report.',
        topPerformerInsights: 'Unable to generate AI insights at this time. Please try regenerating the report.',
        keyRecommendations: 'Unable to generate AI insights at this time. Please try regenerating the report.'
      });
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  // Generate report function
  // Start loading timer
  const startLoadingTimer = () => {
    setLoadingProgress(0);
    const timer = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prev + (100 / 60); // Increment every second for 60 seconds
      });
    }, 1000);
    setLoadingTimer(timer);
    return timer;
  };

  // Stop loading timer
  const stopLoadingTimer = () => {
    if (loadingTimer) {
      clearInterval(loadingTimer);
      setLoadingTimer(null);
    }
    setLoadingProgress(0);
  };

  const generateReport = async () => {
    setIsGenerating(true);
    setIsLoading(true);

    // Start the 60-second progress timer
    const timer = startLoadingTimer();

    try {
      // Get current date range and account ID
      const dateCondition = getSQLDateRange();
      const accountId = getAccountId();

      // First get total spend for calculating 0.1% threshold
      // Calculate AOV and determine spend threshold
      const aovResponse = await axios.post('/api/chat/sql', {
        query: `SELECT 
          SUM(spend) as total_spend,
          ROUND((SUM(purchase_value)::float / NULLIF(SUM(purchases), 0))::numeric, 2) as aov
        FROM t_ad_daily_performance 
        WHERE raw_account_id = '${accountId}' AND ${dateCondition} AND purchases > 0`,
        accountId: accountId
      });
      
      const totalSpend = aovResponse.data.data?.[0]?.total_spend || 0;
      const aov = aovResponse.data.data?.[0]?.aov || 100; // Default to $100 if AOV can't be calculated
      const minSpendThreshold = Math.max(100, 1 * aov); // Minimum of $100 or 1 * AOV

      // Load all data in parallel
      const [
        metricsResponse,
        adTypesResponse,
        featuresTopResponse,
        featuresBottomResponse,
        topContentResponse
      ] = await Promise.allSettled([
        // Basic metrics
        axios.post('/api/chat/sql', {
          query: `SELECT SUM(spend) as total_spend, SUM(purchase_value) as total_revenue, COUNT(DISTINCT raw_ad_id) as total_ads, ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as avg_roas FROM t_ad_daily_performance WHERE raw_account_id = '${accountId}' AND ${dateCondition}`,
          accountId: accountId
        }),

        // Ad Types with ROAS analysis and asset URLs
        axios.post('/api/chat/sql', {
          query: `SELECT 
            COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') as ad_type,
            COUNT(DISTINCT a.raw_ad_id) as count,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 2) as ctr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            array_agg(DISTINCT COALESCE(il.url, vl.url)) as asset_urls,
            array_agg(DISTINCT CASE 
                WHEN il.f_ad_type IS NOT NULL THEN 'Image'
                WHEN vl.video_ad_type IS NOT NULL THEN 'Video'
            END) as media_types,
            array_agg(DISTINCT COALESCE(il.f_description, vl.cf_visual_description)) FILTER (WHERE COALESCE(il.f_description, vl.cf_visual_description) IS NOT NULL) as descriptions
          FROM t_ad a
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_daily_performance adp ON adp.raw_ad_id = a.raw_ad_id AND ${dateCondition.replace('date >=', 'adp.date >=')}
          WHERE a.raw_account_id = '${accountId}' 
          AND adp.raw_account_id = '${accountId}'
          AND a.raw_ad_id IN (SELECT DISTINCT raw_ad_id FROM t_ad_daily_performance WHERE raw_account_id = '${accountId}' AND ${dateCondition})
          AND COALESCE(il.url, vl.url) IS NOT NULL
          AND COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') NOT ILIKE '%none%'
          AND COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') NOT ILIKE '%not applicable%'
          AND COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') NOT ILIKE '%n/a%'
          GROUP BY COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown')
          ORDER BY total_spend DESC`,
          accountId: accountId
        }),

        // Creative Features analysis with asset URLs - Top performers (High ROAS)
        axios.post('/api/chat/sql', {
          query: `SELECT 
            feature_name, 
            feature_type, 
            feature_value, 
            COUNT(DISTINCT raw_ad_id) as number_of_ads,
            SUM(spend) as total_spend,
            SUM(impressions) as total_impressions,
            SUM(clicks) as total_clicks,
            ROUND((SUM(clicks)::float / NULLIF(SUM(impressions), 0) * 100)::numeric, 2) as ctr,
            ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as roas,
            array_agg(DISTINCT creative_url ORDER BY creative_url) FILTER (WHERE creative_url IS NOT NULL) as asset_urls,
            array_agg(DISTINCT media_type ORDER BY media_type) FILTER (WHERE media_type IS NOT NULL) as media_types,
            array_agg(DISTINCT description ORDER BY description) FILTER (WHERE description IS NOT NULL) as descriptions
          FROM (
            SELECT DISTINCT 
              a.raw_ad_id,
              COALESCE(il.url, vl.url) as creative_url,
              CASE 
                  WHEN il.f_ad_type IS NOT NULL THEN 'Image'
                  WHEN vl.video_ad_type IS NOT NULL THEN 'Video'
              END as media_type,
              unnest(ARRAY[
                -- Visual Features (16)
                'Visual: Product Position', 'Visual: Setting', 'Visual: Lighting', 'Visual: Dominant Color', 
                'Visual: Person Position', 'Visual: Logo Position', 'Visual: Camera Work', 'Visual: Product Presentation',
                'Visual: Bright Colors', 'Visual: Professional Style', 'Visual: UGC Style', 'Visual: Face Present', 
                'Visual: Multiple Subjects', 'Visual: Celebrity Shown', 'Visual: Product Shown', 'Visual: Before After',
                -- Audio Features (9)
                'Audio: Voice Tone', 'Audio: Music Mood', 'Audio: Speaking Pace', 'Audio: Voice Gender', 'Audio: Voice Age',
                'Audio: Music Genre', 'Audio: Sound Effects', 'Audio: Voiceover', 'Audio: Music Playing',
                -- Text Features (8)
                'Text: Alignment', 'Text: Position', 'Text: Size', 'Text: Color', 'Text: Font', 'Text: Weight',
                'Text: Overlay Presence', 'Text: Animation',
                -- Content Features (7)
                'Content: Ad Type', 'Content: Category', 'Content: Value Proposition', 'Content: Call to Action Type',
                'Content: Price Display', 'Content: Promotion Type', 'Content: Social Proof Type',
                -- Talent Features (5)
                'Talent: Age Range', 'Talent: Gender', 'Talent: Type', 'Talent: Actions', 'Talent: Face Close Up'
              ]) as feature_name,
              'Creative Feature' as feature_type,
              unnest(ARRAY[
                -- Visual Features (16)
                COALESCE(il.f_product_position, vl.cf_product_position), COALESCE(il.f_setting, vl.cf_setting), 
                COALESCE(il.f_lighting, vl.cf_lighting), COALESCE(il.f_dominant_color, vl.cf_dominant_color),
                COALESCE(il.f_person_position, vl.cf_person_position), COALESCE(il.f_logo_position, vl.cf_logo_position),
                COALESCE(il.f_camera_work, vl.cf_camera_work), COALESCE(il.f_product_presentation, vl.cf_product_presentation),
                COALESCE(il.f_bright_colors::text, vl.cf_bright_colors::text), il.f_professional_style::text, il.f_ugc_style::text,
                COALESCE(il.f_face_present::text, vl.cf_creator_face_close_up::text), COALESCE(il.f_multiple_subjects::text, vl.cf_multiple_creator_shown::text),
                COALESCE(il.f_celebrity_shown::text, vl.cf_celebrity_shown::text), COALESCE(il.f_product_shown::text, vl.cf_product_demonstrated::text),
                COALESCE(il.f_before_after_visual::text, vl.cf_before_after_benefits_demonstrated::text),
                -- Audio Features (9)
                vl.cf_voice_tone, vl.cf_music_mood, vl.cf_speaking_pace, vl.cf_voice_gender, vl.cf_voice_age,
                vl.cf_music_genre, vl.cf_sound_effects, vl.cf_voiceover::text, vl.cf_music_playing::text,
                -- Text Features (8)
                COALESCE(il.f_text_alignment, vl.cf_text_alignment), COALESCE(il.f_text_position, vl.cf_text_position),
                COALESCE(il.f_text_size, vl.cf_text_size), COALESCE(il.f_text_color, vl.cf_text_color),
                COALESCE(il.f_text_font, vl.cf_text_font), COALESCE(il.f_text_weight, vl.cf_text_weight),
                COALESCE(il.f_text_overlay_presence::text, vl.cf_text_overlay_presence::text), vl.cf_text_animation,
                -- Content Features (7)
                COALESCE(il.f_ad_type, vl.video_ad_type), COALESCE(il.f_content_category, vl.cf_content_category),
                COALESCE(il.f_value_proposition, vl.video_value_proposition), COALESCE(il.f_call_to_action_type, vl.cf_call_to_action_type),
                COALESCE(il.f_price_display, vl.cf_price_display), COALESCE(il.f_promotion_type, vl.cf_promotion_type),
                COALESCE(il.f_social_proof_type, vl.cf_social_proof_type),
                -- Talent Features (5)
                COALESCE(il.f_talent_age_range, vl.cf_talent_age_range), COALESCE(il.f_talent_gender, vl.cf_talent_gender),
                COALESCE(il.f_talent_type, vl.cf_talent_type), COALESCE(il.f_talent_actions, vl.cf_talent_actions),
                vl.cf_creator_face_close_up::text
              ]) as feature_value,
              adp.spend,
              adp.impressions,
              adp.clicks,
              adp.purchase_value,
              adp.roas,
              COALESCE(il.f_description, vl.cf_visual_description) as description
            FROM t_ad a
            LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
            LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
            JOIN (
              SELECT raw_ad_id, 
                     SUM(spend) as spend, 
                     SUM(impressions) as impressions, 
                     SUM(clicks) as clicks, 
                     SUM(purchase_value) as purchase_value,
                     ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as roas
              FROM t_ad_daily_performance 
              WHERE raw_account_id = '${accountId}' AND ${dateCondition}
              GROUP BY raw_ad_id
            ) adp ON a.raw_ad_id = adp.raw_ad_id
            WHERE a.raw_account_id = '${accountId}' 
            AND adp.spend > 0
            AND COALESCE(il.url, vl.url) IS NOT NULL
          ) features
          WHERE feature_value IS NOT NULL 
          AND feature_value != '' 
          AND feature_value NOT ILIKE '%non applicable%'
          AND feature_value NOT ILIKE '%none%'
          AND feature_value NOT ILIKE '%n/a%'
          AND feature_value NOT ILIKE '%not applicable%'
          AND feature_value NOT ILIKE '%null%'
          GROUP BY feature_name, feature_type, feature_value
          HAVING SUM(impressions) >= 1000 AND COUNT(DISTINCT raw_ad_id) >= 2 AND SUM(spend) >= ${minSpendThreshold}
          ORDER BY roas DESC, total_spend DESC
          LIMIT 50`,
          accountId: accountId
        }),

                // Creative Features analysis with asset URLs - Bottom performers (Low ROAS)
        axios.post('/api/chat/sql', {
          query: `SELECT 
            feature_name, 
            feature_type, 
            feature_value, 
            COUNT(DISTINCT raw_ad_id) as number_of_ads,
            SUM(spend) as total_spend,
            SUM(impressions) as total_impressions,
            SUM(clicks) as total_clicks,
            ROUND((SUM(clicks)::float / NULLIF(SUM(impressions), 0) * 100)::numeric, 2) as ctr,
            ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as roas,
            array_agg(DISTINCT creative_url ORDER BY creative_url) FILTER (WHERE creative_url IS NOT NULL) as asset_urls,
            array_agg(DISTINCT media_type ORDER BY media_type) FILTER (WHERE media_type IS NOT NULL) as media_types,
            array_agg(DISTINCT description ORDER BY description) FILTER (WHERE description IS NOT NULL) as descriptions
          FROM (
            SELECT DISTINCT 
              a.raw_ad_id,
              COALESCE(il.url, vl.url) as creative_url,
              CASE 
                  WHEN il.f_ad_type IS NOT NULL THEN 'Image'
                  WHEN vl.video_ad_type IS NOT NULL THEN 'Video'
              END as media_type,
              unnest(ARRAY[
                -- Visual Features (16)
                'Visual: Product Position', 'Visual: Setting', 'Visual: Lighting', 'Visual: Dominant Color', 
                'Visual: Person Position', 'Visual: Logo Position', 'Visual: Camera Work', 'Visual: Product Presentation',
                'Visual: Bright Colors', 'Visual: Professional Style', 'Visual: UGC Style', 'Visual: Face Present', 
                'Visual: Multiple Subjects', 'Visual: Celebrity Shown', 'Visual: Product Shown', 'Visual: Before After',
                -- Audio Features (9)
                'Audio: Voice Tone', 'Audio: Music Mood', 'Audio: Speaking Pace', 'Audio: Voice Gender', 'Audio: Voice Age',
                'Audio: Music Genre', 'Audio: Sound Effects', 'Audio: Voiceover', 'Audio: Music Playing',
                -- Text Features (8)
                'Text: Alignment', 'Text: Position', 'Text: Size', 'Text: Color', 'Text: Font', 'Text: Weight',
                'Text: Overlay Presence', 'Text: Animation',
                -- Content Features (7)
                'Content: Ad Type', 'Content: Category', 'Content: Value Proposition', 'Content: Call to Action Type',
                'Content: Price Display', 'Content: Promotion Type', 'Content: Social Proof Type',
                -- Talent Features (5)
                'Talent: Age Range', 'Talent: Gender', 'Talent: Type', 'Talent: Actions', 'Talent: Face Close Up'
              ]) as feature_name,
              'Creative Feature' as feature_type,
              unnest(ARRAY[
                -- Visual Features (16)
                COALESCE(il.f_product_position, vl.cf_product_position), COALESCE(il.f_setting, vl.cf_setting), 
                COALESCE(il.f_lighting, vl.cf_lighting), COALESCE(il.f_dominant_color, vl.cf_dominant_color),
                COALESCE(il.f_person_position, vl.cf_person_position), COALESCE(il.f_logo_position, vl.cf_logo_position),
                COALESCE(il.f_camera_work, vl.cf_camera_work), COALESCE(il.f_product_presentation, vl.cf_product_presentation),
                COALESCE(il.f_bright_colors::text, vl.cf_bright_colors::text), il.f_professional_style::text, il.f_ugc_style::text,
                COALESCE(il.f_face_present::text, vl.cf_creator_face_close_up::text), COALESCE(il.f_multiple_subjects::text, vl.cf_multiple_creator_shown::text),
                COALESCE(il.f_celebrity_shown::text, vl.cf_celebrity_shown::text), COALESCE(il.f_product_shown::text, vl.cf_product_demonstrated::text),
                COALESCE(il.f_before_after_visual::text, vl.cf_before_after_benefits_demonstrated::text),
                -- Audio Features (9)
                vl.cf_voice_tone, vl.cf_music_mood, vl.cf_speaking_pace, vl.cf_voice_gender, vl.cf_voice_age,
                vl.cf_music_genre, vl.cf_sound_effects, vl.cf_voiceover::text, vl.cf_music_playing::text,
                -- Text Features (8)
                COALESCE(il.f_text_alignment, vl.cf_text_alignment), COALESCE(il.f_text_position, vl.cf_text_position),
                COALESCE(il.f_text_size, vl.cf_text_size), COALESCE(il.f_text_color, vl.cf_text_color),
                COALESCE(il.f_text_font, vl.cf_text_font), COALESCE(il.f_text_weight, vl.cf_text_weight),
                COALESCE(il.f_text_overlay_presence::text, vl.cf_text_overlay_presence::text), vl.cf_text_animation,
                -- Content Features (7)
                COALESCE(il.f_ad_type, vl.video_ad_type), COALESCE(il.f_content_category, vl.cf_content_category),
                COALESCE(il.f_value_proposition, vl.video_value_proposition), COALESCE(il.f_call_to_action_type, vl.cf_call_to_action_type),
                COALESCE(il.f_price_display, vl.cf_price_display), COALESCE(il.f_promotion_type, vl.cf_promotion_type),
                COALESCE(il.f_social_proof_type, vl.cf_social_proof_type),
                -- Talent Features (5)
                COALESCE(il.f_talent_age_range, vl.cf_talent_age_range), COALESCE(il.f_talent_gender, vl.cf_talent_gender),
                COALESCE(il.f_talent_type, vl.cf_talent_type), COALESCE(il.f_talent_actions, vl.cf_talent_actions),
                vl.cf_creator_face_close_up::text
              ]) as feature_value,
              adp.spend,
              adp.impressions,
              adp.clicks,
              adp.purchase_value,
              adp.roas,
              COALESCE(il.f_description, vl.cf_visual_description) as description
            FROM t_ad a
            LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
            LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
            JOIN (
              SELECT raw_ad_id, 
                     SUM(spend) as spend, 
                     SUM(impressions) as impressions, 
                     SUM(clicks) as clicks, 
                     SUM(purchase_value) as purchase_value,
                     ROUND((SUM(purchase_value)::float / NULLIF(SUM(spend), 0))::numeric, 2) as roas
              FROM t_ad_daily_performance 
              WHERE raw_account_id = '${accountId}' AND ${dateCondition}
              GROUP BY raw_ad_id
              ) adp ON a.raw_ad_id = adp.raw_ad_id
            WHERE a.raw_account_id = '${accountId}' 
            AND adp.spend > 0
            AND COALESCE(il.url, vl.url) IS NOT NULL
          ) features
          WHERE feature_value IS NOT NULL 
          AND feature_value != '' 
          AND feature_value NOT ILIKE '%non applicable%'
          AND feature_value NOT ILIKE '%none%'
          AND feature_value NOT ILIKE '%n/a%'
          AND feature_value NOT ILIKE '%not applicable%'
          AND feature_value NOT ILIKE '%null%'
          GROUP BY feature_name, feature_type, feature_value
          HAVING SUM(impressions) >= 1000 AND COUNT(DISTINCT raw_ad_id) >= 2 AND SUM(spend) >= ${minSpendThreshold}
          ORDER BY roas ASC, total_spend DESC
          LIMIT 50`,
          accountId: accountId
        }),

        // Top performing content with asset URLs - ranked by highest ROAS with at least 0.1% of total spending
        axios.post('/api/chat/sql', {
          query: `SELECT 
            a.name as ad_name, 
            COALESCE(il.url, vl.url) as asset_url,
            CASE 
                WHEN il.f_ad_type IS NOT NULL THEN 'Image'
                WHEN vl.video_ad_type IS NOT NULL THEN 'Video'
            END as media_type,
            SUM(adp.impressions) as total_impressions, 
            SUM(adp.clicks) as total_clicks, 
            SUM(adp.spend) as total_spend, 
            SUM(adp.purchase_value) as total_revenue, 
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 2) as ctr, 
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas 
          FROM t_ad_daily_performance adp 
          JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id 
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}' 
          AND a.raw_account_id = '${accountId}' 
          AND ${dateCondition.replace('date', 'adp.date')}
          AND COALESCE(il.url, vl.url) IS NOT NULL
          GROUP BY a.raw_ad_id, a.name, COALESCE(il.url, vl.url), il.f_ad_type, vl.video_ad_type
          HAVING SUM(adp.spend) >= ${minSpendThreshold}
          ORDER BY roas DESC 
          LIMIT 10`,
          accountId: accountId
        })
      ]);

      // Process metrics data
      let metrics = {};
      if (metricsResponse.status === 'fulfilled' && metricsResponse.value.data.success && metricsResponse.value.data.data && metricsResponse.value.data.data.length > 0) {
        const row = metricsResponse.value.data.data[0];
        metrics = {
          totalSpend: parseFloat(row.total_spend) || 0,
          totalRevenue: parseFloat(row.total_revenue) || 0,
          totalAds: parseInt(row.total_ads) || 0,
          avgROAS: parseFloat(row.avg_roas) || 0
        };
      }

      // Process ad types data
      let adTypes = [];
      if (adTypesResponse.status === 'fulfilled' && adTypesResponse.value.data.success && adTypesResponse.value.data.data) {
        adTypes = adTypesResponse.value.data.data
          .filter(row => row.ad_type && row.ad_type !== 'Unknown')
          .map(row => ({
            name: row.ad_type,
            count: parseInt(row.count) || 0,
            spend: parseFloat(row.total_spend) || 0,
            impressions: parseInt(row.total_impressions) || 0,
            roas: parseFloat(row.roas) || 0,
            ctr: parseFloat(row.ctr) || 0,
            revenue: parseFloat(row.total_revenue) || 0,
            assetUrls: row.asset_urls || [],
            mediaTypes: row.media_types || [],
            descriptions: row.descriptions || []
          }));
      }

      // Process features data - Top performers
      let featuresTop = [];
      if (featuresTopResponse.status === 'fulfilled' && featuresTopResponse.value.data.success && featuresTopResponse.value.data.data) {
        featuresTop = featuresTopResponse.value.data.data
          .filter(row => row.feature_value && row.feature_value !== 'Unknown')
          .map(row => ({
            name: `${row.feature_name}: ${row.feature_value}`,
            feature: row.feature_value,
            count: parseInt(row.number_of_ads) || 0,
            spend: parseFloat(row.total_spend) || 0,
            impressions: parseInt(row.total_impressions) || 0,
            roas: parseFloat(row.roas) || 0,
            ctr: parseFloat(row.ctr) || 0,
            assetUrls: row.asset_urls || [],
            mediaTypes: row.media_types || [],
            descriptions: row.descriptions || [],
            isTopPerformer: true
          }));
      }

      // Process features data - Bottom performers
      let featuresBottom = [];
      if (featuresBottomResponse.status === 'fulfilled' && featuresBottomResponse.value.data.success && featuresBottomResponse.value.data.data) {
        featuresBottom = featuresBottomResponse.value.data.data
          .filter(row => row.feature_value && row.feature_value !== 'Unknown')
          .map(row => ({
            name: `${row.feature_name}: ${row.feature_value}`,
            feature: row.feature_value,
            count: parseInt(row.number_of_ads) || 0,
            spend: parseFloat(row.total_spend) || 0,
            impressions: parseInt(row.total_impressions) || 0,
            roas: parseFloat(row.roas) || 0,
            ctr: parseFloat(row.ctr) || 0,
            assetUrls: row.asset_urls || [],
            mediaTypes: row.media_types || [],
            descriptions: row.descriptions || [],
            isTopPerformer: false
          }));
      }

      // Combine features for overall analysis
      let features = [...featuresTop, ...featuresBottom];

      // Process top content data
      let topContent = [];
      if (topContentResponse.status === 'fulfilled' && topContentResponse.value.data.success && topContentResponse.value.data.data) {
        topContent = topContentResponse.value.data.data
          .filter(row => row.ad_name && row.roas > 0)
          .map(row => ({
            name: row.ad_name,
            spend: parseFloat(row.total_spend) || 0,
            impressions: parseInt(row.total_impressions) || 0,
            roas: parseFloat(row.roas) || 0,
            ctr: parseFloat(row.ctr) || 0,
            revenue: parseFloat(row.total_revenue) || 0,
            assetUrl: row.asset_url,
            mediaType: row.media_type
          }));
      }

      // Calculate ROAS impact (simplified calculation)
      const avgROAS = metrics.avgROAS || (metrics.totalRevenue / metrics.totalSpend);
      const calculateImpact = (roas) => {
        if (!roas || !avgROAS || avgROAS === 0) return 0;
        return ((roas - avgROAS) / avgROAS) * 100;
      };

      // Add impact calculations
      adTypes = adTypes.map(item => ({
        ...item,
        impact: calculateImpact(item.roas)
      }));

      // Add impact calculations to both top and bottom features
      featuresTop = featuresTop.map(item => ({
        ...item,
        impact: calculateImpact(item.roas)
      }));
      
      featuresBottom = featuresBottom.map(item => ({
        ...item,
        impact: calculateImpact(item.roas)
      }));

      features = features.map(item => ({
        ...item,
        impact: calculateImpact(item.roas)
      }));

      // Separate best and worst performers (show all ad types)
      const bestAdTypes = adTypes.filter(item => item.impact > 0).sort((a, b) => b.impact - a.impact);
      const worstAdTypes = adTypes.filter(item => item.impact < 0).sort((a, b) => a.impact - b.impact);
      
      // Use the actual top and bottom performers from separate queries
      // Fallback to overall features if filtered queries return no results
      const bestFeatures = featuresTop.length > 0 ? featuresTop.slice(0, 4) : features.filter(item => item.impact > 0).sort((a, b) => b.impact - a.impact).slice(0, 4);
      const worstFeatures = featuresBottom.length > 0 ? featuresBottom.slice(0, 4) : features.filter(item => item.impact < 0).sort((a, b) => a.impact - b.impact).slice(0, 4);
      
      console.log('Feature data:', { 
        featuresTop: featuresTop.length, 
        featuresBottom: featuresBottom.length,
        bestFeatures: bestFeatures.length,
        worstFeatures: worstFeatures.length,
        featuresTopSample: featuresTop.slice(0, 2),
        featuresBottomSample: featuresBottom.slice(0, 2)
      });
      
      // Remove duplicates between top and bottom performers
      const bestFeatureNames = bestFeatures.map(f => f.name);
      const worstFeaturesFiltered = worstFeatures.filter(f => !bestFeatureNames.includes(f.name));
      
      // If we don't have enough unique bottom performers, get more from the overall features
      if (worstFeaturesFiltered.length < 4) {
        const remainingWorst = features
          .filter(item => item.impact < 0 && !bestFeatureNames.includes(item.name))
          .sort((a, b) => a.impact - b.impact)
          .slice(0, 4 - worstFeaturesFiltered.length);
        worstFeaturesFiltered.push(...remainingWorst);
      }
      
      console.log('Final feature arrays:', {
        bestFeatures: bestFeatures.length,
        worstFeaturesFiltered: worstFeaturesFiltered.length,
        visualFeatures: bestFeatures.length + worstFeaturesFiltered.length
      });

      // Create visual features array, fallback to original features if empty
      const visualFeaturesArray = [...bestFeatures, ...worstFeaturesFiltered];
      const finalVisualFeatures = visualFeaturesArray.length > 0 ? visualFeaturesArray : features;
      
      console.log('Setting visualFeatures:', {
        visualFeaturesArray: visualFeaturesArray.length,
        finalVisualFeatures: finalVisualFeatures.length
      });
      
      setReportData({
        metrics,
        adTypes,
        features,
        topContent,
        bestAdTypes,
        worstAdTypes,
        bestFeatures,
        worstFeatures: worstFeaturesFiltered,
        // Separate data for visual analysis to avoid duplicates
        visualFeatures: finalVisualFeatures
      });

      setDashboardInfo({
        title: "Creative Performance Analysis",
        lastUpdated: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        totalSpend: metrics.totalSpend,
        averageROAS: metrics.avgROAS,
        analyzedCreatives: metrics.totalAds
      });

      // Generate AI insights
      console.log('About to generate AI insights with data:', {
        metrics,
        bestAdTypes: bestAdTypes.length,
        worstAdTypes: worstAdTypes.length,
        bestFeatures: bestFeatures.length,
        worstFeatures: worstFeaturesFiltered.length
      });
      
      await generateAIInsights({
        metrics,
        bestAdTypes,
        worstAdTypes,
        bestFeatures,
        worstFeatures: worstFeaturesFiltered
      });

      toast.success('Creative report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      // Stop the loading timer
      stopLoadingTimer();
      setIsGenerating(false);
      setIsLoading(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sorting function for top performers table
  const handleTopPerformersSort = (key) => {
    let direction = 'asc';
    if (topPerformersSortConfig.key === key && topPerformersSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTopPerformersSortConfig({ key, direction });
  };

  // Helper function to get sorted top performers data
  const getSortedTopPerformers = () => {
    if (!reportData || !reportData.topContent) return [];
    
    let data = [...reportData.topContent];
    
    // Apply sorting if configured
    if (topPerformersSortConfig.key) {
      data.sort((a, b) => {
        let aValue = a[topPerformersSortConfig.key];
        let bValue = b[topPerformersSortConfig.key];
        
        // Handle numeric values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return topPerformersSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Handle string values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return topPerformersSortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    return data;
  };

  // Helper function to get the right visual data based on active subtab
  const getVisualData = () => {
    if (!reportData) return [];
    
    console.log('getVisualData called:', { visualSubTab, reportData });
    
    let data = [];
    switch(visualSubTab) {
      case 'adTypes': 
        console.log('Returning adTypes:', reportData.adTypes);
        data = reportData.adTypes || [];
        break;
      case 'features': 
        console.log('Returning all features for detailed analysis');
        // For detailed analysis, return all features (top 50)
        data = reportData.features || [];
        break;
      default: 
        console.log('Returning default adTypes:', reportData.adTypes);
        data = reportData.adTypes || [];
        break;
    }
    
    // Apply sorting if configured
    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle numeric values
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Handle string values
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    // Limit to 50 items for detailed analysis
    return data.slice(0, 50);
  };

  // Get color for impact
  const getColorForImpact = (impact) => {
    if (impact > 0) return '#10b981'; // green
    if (impact < 0) return '#ef4444'; // red
    return '#6b7280'; // gray
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md w-full px-6">
          <div className="mb-6">
            <Sparkles className="h-12 w-12 text-purple-400 mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Generating Creative Report</h2>
            <p className="text-gray-600">Analyzing your creative performance data...</p>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
          
          {/* Timer Display */}
          <div className="flex justify-between text-sm text-gray-500">
            <span>Progress: {Math.round(loadingProgress)}%</span>
            <span>Time: {Math.round(loadingProgress * 0.6)}s / 60s</span>
          </div>
          
          {/* Loading Tips */}
          <div className="mt-6 p-4 bg-white rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">
              <strong>💡 Tip:</strong> We're analyzing {reportData?.totalAds || 'thousands of'} ads across multiple creative features. 
              This comprehensive analysis typically takes 45-60 seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 p-4 sm:p-6 min-h-screen w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <Sparkles className="h-8 w-8 text-purple-400 mr-3" />
              Creative Performance Report - {getAccountDisplayName()}
            </h1>
            <p className="text-gray-600">
              Last Updated: {dashboardInfo.lastUpdated} | Analyzed Metric: ROAS
            </p>
          </div>
          
          {/* Date Range Selector */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">{formatDateRange()}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {getPresetRanges().map((preset) => (
                  <button
                    key={preset.days}
                    onClick={() => handlePresetRange(preset.days)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      dateRangeType === 'preset' && selectedRange === preset.days
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {preset.days === 365 ? '1y' : preset.days === 730 ? '2y' : `${preset.days}d`}
                  </button>
                ))}
                <button
                  onClick={() => setDateRangeType('custom')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    dateRangeType === 'custom'
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Custom
                </button>
                {reportData && (
                  <>
                    <button
                      onClick={() => navigate('/comprehensive-creative-report')}
                      className="px-3 py-1 rounded-lg text-sm font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
                    >
                      📊 Comprehensive
                    </button>
                    <button
                      onClick={handleShareReport}
                      disabled={isSharing}
                      className="px-3 py-1 rounded-lg text-sm font-medium transition-colors bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSharing ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                          Sharing...
                        </div>
                      ) : (
                        <>
                          <Share2 className="h-4 w-4 inline mr-1" />
                          Share
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Custom Date Range Inputs */}
            {dateRangeType === 'custom' && (
              <div className="flex flex-col sm:flex-row gap-3 items-end p-3 bg-gray-50 rounded-lg">
                <div>
                  <label htmlFor="start-date" className="block text-xs font-medium text-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="end-date" className="block text-xs font-medium text-gray-600 mb-1">
                    End Date
                  </label>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                {startDate && endDate && (
                  <button
                    onClick={() => handleCustomDateRange(startDate, endDate)}
                    className="bg-purple-600 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors"
                  >
                    Apply
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generate Report Button */}
      {!reportData && (
        <div className="text-center mb-8">
          <button
            onClick={generateReport}
            disabled={isGenerating}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 transform hover:scale-105"
          >
            {isGenerating ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Generating Report...
              </div>
            ) : (
              <div className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Generate Creative Report
              </div>
            )}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Analyze your ad performance data and get actionable creative insights
          </p>
        </div>
      )}

      {reportData && (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8 w-full">
            <div className="text-center p-4 sm:p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm font-medium mb-2">Total Spend</p>
              <p className="text-2xl sm:text-3xl font-bold text-blue-700">{formatDollar(dashboardInfo.totalSpend)}</p>
            </div>
            <div className="text-center p-4 sm:p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm font-medium mb-2">Average ROAS</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-700">{dashboardInfo.averageROAS.toFixed(2)}x</p>
            </div>
            <div className="text-center p-4 sm:p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm font-medium mb-2">Analyzed Creatives</p>
              <p className="text-2xl sm:text-3xl font-bold text-purple-700">{dashboardInfo.analyzedCreatives.toLocaleString()}</p>
            </div>
            <div className="text-center p-4 sm:p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm font-medium mb-2">Best Element Impact</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-700">
                {reportData.bestAdTypes.length > 0 ? formatPercent(reportData.bestAdTypes[0].impact) : 
                 reportData.bestFeatures.length > 0 ? formatPercent(reportData.bestFeatures[0].impact) : '+0%'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {reportData.bestAdTypes.length > 0 ? reportData.bestAdTypes[0].name : 
                 reportData.bestFeatures.length > 0 ? reportData.bestFeatures[0].name : 'No data'}
              </p>
            </div>
          </div>

          {/* Main Tabs */}
          <div className="flex flex-wrap border-b mb-6">
            <button
              className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'overview' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
              onClick={() => setActiveTab('overview')}>
              Executive Summary
            </button>
            <button
              className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'topPerformers' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
              onClick={() => setActiveTab('topPerformers')}>
              Top Performers
            </button>
            <button
              className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'visual' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
              onClick={() => setActiveTab('visual')}>
              Creative Analysis
            </button>
            <button
              className={`py-3 px-4 sm:px-6 font-medium transition-colors border-b-2 ${activeTab === 'insights' ? 'text-blue-600 border-blue-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
              onClick={() => setActiveTab('insights')}>
              <Sparkles className="h-4 w-4 inline mr-1" />
              Creative Insights
            </button>

          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow p-6 mb-6 flex-grow w-full">
            {/* Executive Summary Tab */}
            {activeTab === 'overview' && (
              <div>
                <h2 className="text-xl font-bold mb-4 text-gray-800">Executive Summary</h2>
                {/* Self-adaptive responsive design: Content flows naturally to fill available width */}

                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8 w-full">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 sm:p-6 rounded-xl border border-green-100 flex-1 min-w-0">
                    <div className="flex items-center mb-5">
                      <div className="p-2 bg-green-100 rounded-lg mr-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <h3 className="text-lg font-bold text-green-800">TOP PERFORMING AD TYPES</h3>
                    </div>
                    <div className="space-y-4">
                      {reportData.bestAdTypes.map((item, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                          <div className="flex-1 flex flex-col justify-center">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center flex-1">
                                <span className="font-semibold text-gray-900">{item.name}</span>
                                {item.name.toLowerCase().includes('video') && (
                                  <Play className="h-4 w-4 text-blue-600 ml-2" />
                                )}
                                {item.name.toLowerCase().includes('image') && (
                                  <Image className="h-4 w-4 text-purple-600 ml-2" />
                                )}
                              </div>
                              <span className="text-green-600 font-bold text-lg">{formatPercent(item.impact)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-gray-600">
                              <span className="bg-blue-50 px-2 py-1 rounded-md">ROAS: {item.roas.toFixed(2)}x</span>
                              <span className="bg-gray-50 px-2 py-1 rounded-md">Spend: {formatDollar(item.spend)}</span>
                            </div>
                            {renderFeatureExamples(item.name, item, true)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 sm:p-6 rounded-xl border border-red-100 flex-1 min-w-0">
                    <div className="flex items-center mb-5">
                      <div className="p-2 bg-red-100 rounded-lg mr-3">
                        <XCircle className="h-5 w-5 text-red-600" />
                      </div>
                      <h3 className="text-lg font-bold text-red-800">UNDERPERFORMING AD TYPES</h3>
                    </div>
                    <div className="space-y-4">
                      {reportData.worstAdTypes.map((item, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                          <div className="flex-1 flex flex-col justify-center">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center flex-1">
                                <span className="font-semibold text-gray-900">{item.name}</span>
                                {item.name.toLowerCase().includes('video') && (
                                  <Play className="h-4 w-4 text-blue-600 ml-2" />
                                )}
                                {item.name.toLowerCase().includes('image') && (
                                  <Image className="h-4 w-4 text-purple-600 ml-2" />
                                )}
                              </div>
                              <span className="text-red-600 font-bold text-lg">{formatPercent(item.impact)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-gray-600">
                              <span className="bg-blue-50 px-2 py-1 rounded-md">ROAS: {item.roas.toFixed(2)}x</span>
                              <span className="bg-gray-50 px-2 py-1 rounded-md">Spend: {formatDollar(item.spend)}</span>
                            </div>
                            {renderFeatureExamples(item.name, item, false)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8 w-full">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 sm:p-6 rounded-xl border border-green-100 flex-1 min-w-0">
                    <div className="flex items-center mb-5">
                      <div className="p-2 bg-green-100 rounded-lg mr-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <h3 className="text-lg font-bold text-green-800">TOP PERFORMING FEATURES</h3>
                    </div>
                    <div className="space-y-4">
                      {reportData.bestFeatures.map((item, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                          <div className="flex-1 flex flex-col justify-center">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center flex-1">
                                <span className="font-semibold text-gray-900">{item.name}</span>
                                {item.name.toLowerCase().includes('video') && (
                                  <Play className="h-4 w-4 text-blue-600 ml-2" />
                                )}
                                {item.name.toLowerCase().includes('image') && (
                                  <Image className="h-4 w-4 text-purple-600 ml-2" />
                                )}
                              </div>
                              <span className="text-green-600 font-bold text-lg">{formatPercent(item.impact)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-gray-600">
                              <span className="bg-blue-50 px-2 py-1 rounded-md">ROAS: {item.roas.toFixed(2)}x</span>
                              <span className="bg-gray-50 px-2 py-1 rounded-md">Spend: {formatDollar(item.spend)}</span>
                            </div>
                            {renderFeatureExamples(item.name, item, true)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 sm:p-6 rounded-xl border border-red-100 flex-1 min-w-0">
                    <div className="flex items-center mb-5">
                      <div className="p-2 bg-red-100 rounded-lg mr-3">
                        <XCircle className="h-5 w-5 text-red-600" />
                      </div>
                      <h3 className="text-lg font-bold text-red-800">UNDERPERFORMING FEATURES</h3>
                    </div>
                    <div className="space-y-4">
                      {reportData.worstFeatures.map((item, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                          <div className="flex-1 flex flex-col justify-center">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center flex-1">
                                <span className="font-semibold text-gray-900">{item.name}</span>
                                {item.name.toLowerCase().includes('video') && (
                                  <Play className="h-4 w-4 text-blue-600 ml-2" />
                                )}
                                {item.name.toLowerCase().includes('image') && (
                                  <Image className="h-4 w-4 text-purple-600 ml-2" />
                                )}
                              </div>
                              <span className="text-red-600 font-bold text-lg">{formatPercent(item.impact)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-gray-600">
                              <span className="bg-blue-50 px-2 py-1 rounded-md">ROAS: {item.roas.toFixed(2)}x</span>
                              <span className="bg-gray-50 px-2 py-1 rounded-md">Spend: {formatDollar(item.spend)}</span>
                            </div>
                            {renderFeatureExamples(item.name, item, false)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>


              </div>
            )}

            {/* Top Performers Tab */}
            {activeTab === 'topPerformers' && (
              <div>
                <h2 className="text-xl font-bold mb-4 text-gray-800">Top Performing Ads by ROAS</h2>

                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <p>These ads represent the highest ROAS values in your dataset, ranked by performance efficiency. Only ads with at least 0.1% of total spending are included to ensure meaningful analysis.</p>
                  <p className="text-sm text-blue-600 mt-2">💡 <strong>Tip:</strong> Click on any column header to sort the data. Click again to reverse the sort order.</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-3 px-4 text-left">Rank</th>
                        <th className="py-3 px-4 text-left">Ad Name</th>
                        <th 
                          className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => handleTopPerformersSort('roas')}
                        >
                          ROAS
                          {topPerformersSortConfig.key === 'roas' && (
                            <span className="ml-1 text-blue-600">
                              {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => handleTopPerformersSort('ctr')}
                        >
                          CTR
                          {topPerformersSortConfig.key === 'ctr' && (
                            <span className="ml-1 text-blue-600">
                              {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => handleTopPerformersSort('spend')}
                        >
                          Spend
                          {topPerformersSortConfig.key === 'spend' && (
                            <span className="ml-1 text-blue-600">
                              {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                        <th 
                          className="py-3 px-4 text-right cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => handleTopPerformersSort('impressions')}
                        >
                          Impressions
                          {topPerformersSortConfig.key === 'impressions' && (
                            <span className="ml-1 text-blue-600">
                              {topPerformersSortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedTopPerformers().map((ad, index) => (
                        <tr key={index} className="border-b bg-green-50">
                          <td className="py-3 px-4">{index + 1}</td>
                          <td className="py-3 px-4 font-medium">
                            <div>
                              <div>{ad.name}</div>
                              {renderFeatureExamples(ad.name, ad, true)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-green-600">{ad.roas.toFixed(2)}x</td>
                          <td className="py-3 px-4 text-right">{ad.ctr.toFixed(2)}%</td>
                          <td className="py-3 px-4 text-right">{formatDollar(ad.spend)}</td>
                          <td className="py-3 px-4 text-right">{ad.impressions.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>


              </div>
            )}

            {/* Creative Analysis Tab */}
            {activeTab === 'visual' && (
              <div>
                <h2 className="text-xl font-bold mb-4 text-gray-800">Creative Analysis</h2>

                {/* Visual Subtabs */}
                <div className="flex border-b mb-6">
                  <button
                    className={`py-2 px-4 font-medium ${visualSubTab === 'adTypes' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                    onClick={() => {
                      setVisualSubTab('adTypes');
                      setSortConfig({ key: 'roas', direction: 'desc' });
                    }}>
                    <BarChart3 className="h-4 w-4 inline mr-1" />
                    Ad Types
                  </button>
                  <button
                    className={`py-2 px-4 font-medium ${visualSubTab === 'features' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
                    onClick={() => {
                      setVisualSubTab('features');
                      setSortConfig({ key: 'roas', direction: 'desc' });
                    }}>
                    <Palette className="h-4 w-4 inline mr-1" />
                    Creative Features
                  </button>
                </div>

                {/* Visual Elements Content */}
                <div>
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <p>
                      Comprehensive analysis of {visualSubTab === 'adTypes' ? 'all ad types' : 'all creative features'} and their impact on performance and ROAS. 
                      <strong> Showing up to 50 {visualSubTab === 'adTypes' ? 'ad types' : 'features'}</strong> - click column headers to sort.
                    </p>
                  </div>

                  <div className="mb-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={getVisualData().sort((a, b) => b.impact - a.impact)}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10 }}
                          angle={getVisualData().some(item => item.name && item.name.toString().length > 8) ? -45 : 0}
                          textAnchor={getVisualData().some(item => item.name && item.name.toString().length > 8) ? "end" : "middle"}
                          height={getVisualData().some(item => item.name && item.name.toString().length > 8) ? 80 : 60}
                          interval={0}
                        />
                        <YAxis label={{ value: 'ROAS Impact (%)', angle: -90, position: 'insideLeft', fontSize: 10 }} tick={{ fontSize: 10 }} />
                        <Tooltip
                          formatter={(value, name) => [formatPercent(value), "ROAS Impact"]}
                        />
                        <Bar dataKey="impact" fill={(data) => getColorForImpact(data.impact)} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      Displaying <strong>{getVisualData().length}</strong> {visualSubTab === 'adTypes' ? 'ad types' : 'creative features'}
                      {sortConfig.key && (
                        <span className="ml-2 text-blue-600">
                          (sorted by {sortConfig.key} {sortConfig.direction === 'asc' ? '↑' : '↓'})
                        </span>
                      )}
                    </p>
                  </div>
                  
                  <div className="overflow-x-auto mb-6">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="py-3 px-4 text-left">
                            <button 
                              onClick={() => handleSort('name')}
                              className="flex items-center hover:text-blue-600 transition-colors"
                            >
                              {visualSubTab === 'adTypes' ? 'Ad Type' : 'Feature'}
                              {sortConfig.key === 'name' && (
                                <span className="ml-1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 text-right">
                            <button 
                              onClick={() => handleSort('impact')}
                              className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                            >
                              ROAS Impact
                              {sortConfig.key === 'impact' && (
                                <span className="ml-1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 text-right">
                            <button 
                              onClick={() => handleSort('roas')}
                              className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                            >
                              ROAS Value
                              {sortConfig.key === 'roas' && (
                                <span className="ml-1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 text-right">
                            <button 
                              onClick={() => handleSort('spend')}
                              className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                            >
                              Spend
                              {sortConfig.key === 'spend' && (
                                <span className="ml-1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </button>
                          </th>
                          <th className="py-3 px-4 text-right">
                            <button 
                              onClick={() => handleSort('count')}
                              className="flex items-center justify-end w-full hover:text-blue-600 transition-colors"
                            >
                              Count
                              {sortConfig.key === 'count' && (
                                <span className="ml-1">
                                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVisualData().map((item, index) => (
                          <tr key={index} className={`border-b ${item.impact > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                            <td className="py-3 px-4 font-medium">
                              <div>
                                <div>{item.name}</div>
                                {renderFeatureExamples(item.name, item, item.impact > 0)}
                              </div>
                            </td>
                            <td className={`py-3 px-4 text-right font-bold ${item.impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatPercent(item.impact)}
                            </td>
                            <td className="py-3 px-4 text-right">{item.roas.toFixed(2)}x</td>
                            <td className="py-3 px-4 text-right">{formatDollar(item.spend)}</td>
                            <td className="py-3 px-4 text-right">{item.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>


                </div>
              </div>
            )}

            {/* Creative Insights Tab */}
            {activeTab === 'insights' && (
              <div>
                <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <Sparkles className="h-6 w-6 text-purple-500 mr-3" />
                  AI-Powered Creative Insights
                </h2>

                {!reportData && (
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Generate a Creative Report to unlock AI-powered insights and recommendations</p>
                    <button
                      onClick={generateReport}
                      disabled={isGenerating || isLoading}
                      className="bg-gradient-to-r from-purple-500 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-600 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
                    >
                      {isGenerating || isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Generating Report...
                        </>
                      ) : (
                        <>
                          <BarChart3 className="h-5 w-5 mr-2" />
                          Generate Creative Report
                        </>
                      )}
                    </button>
                  </div>
                )}

                {reportData && (
                  <div className="space-y-8">
                    {/* Key Action Items */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-800">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                        Key Action Items
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        {isGeneratingInsights ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mr-3"></div>
                            <span className="text-gray-600 text-lg">Generating AI-powered insights...</span>
                          </div>
                        ) : (
                          <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                            {aiInsights.keyActionItems || 'Analyzing your performance data to generate actionable insights...'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Top Performer Insights */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-green-800">
                        <BarChart3 className="h-5 w-5 text-green-600 mr-2" />
                        Top Performer Analysis
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        {isGeneratingInsights ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mr-3"></div>
                            <span className="text-gray-600 text-lg">Analyzing top performing elements...</span>
                          </div>
                        ) : (
                          <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                            {aiInsights.topPerformerInsights || 'Identifying patterns and insights from your best performing ads...'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Strategic Recommendations */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-purple-800">
                        <Sparkles className="h-5 w-5 text-purple-600 mr-2" />
                        Strategic Recommendations
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        {isGeneratingInsights ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mr-3"></div>
                            <span className="text-gray-600 text-lg">Generating strategic recommendations...</span>
                          </div>
                        ) : (
                          <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                            {aiInsights.keyRecommendations || 'Preparing strategic recommendations to optimize your campaigns...'}
                          </div>
                        )}
                      </div>
                    </div>


                  </div>
                )}
              </div>
            )}

            {/* Comprehensive Analysis Tab */}
            {activeTab === 'comprehensive' && (
              <div>
                <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <BarChart3 className="h-6 w-6 text-blue-500 mr-3" />
                  Comprehensive Creative Analysis
                </h2>

                {!reportData && (
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">Generate a Creative Report to unlock comprehensive analysis</p>
                    <button
                      onClick={generateReport}
                      disabled={isGenerating || isLoading}
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
                    >
                      {isGenerating || isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Generating Report...
                        </>
                      ) : (
                        <>
                          <BarChart3 className="h-5 w-5 mr-2" />
                          Generate Creative Report
                        </>
                      )}
                    </button>
                  </div>
                )}

                {reportData && (
                  <div className="space-y-8">
                    {/* Ad Name Filter Section */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-purple-800">
                        <Palette className="h-5 w-5 text-purple-600 mr-2" />
                        Ad Name Analysis & Clustering
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          AI-powered analysis of your ad names to identify patterns, themes, and opportunities for optimization.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">Keyword Clusters</h4>
                            <p className="text-sm text-blue-700">Identifying common themes and patterns in ad naming</p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-green-800 mb-2">Performance Ranking</h4>
                            <p className="text-sm text-green-700">Ranking ad names by performance metrics</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Performers Section */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-green-800">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        Top Performers Analysis
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Identify which ads are driving the most impact across video, static, and carousel formats.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">Video Ads</h4>
                            <p className="text-sm text-blue-700">Top performing video creative themes</p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-purple-800 mb-2">Static Ads</h4>
                            <p className="text-sm text-purple-700">Best performing image creative elements</p>
                          </div>
                          <div className="bg-orange-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-orange-800 mb-2">Carousel Ads</h4>
                            <p className="text-sm text-orange-700">Top performing multi-image sequences</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Landing Pages Section */}
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border border-indigo-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-indigo-800">
                        <Maximize2 className="h-5 w-5 text-indigo-600 mr-2" />
                        Landing Page Performance
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Surface which destination pages convert best to guide future campaign direction.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-indigo-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-2">Conversion Leaders</h4>
                            <p className="text-sm text-indigo-700">Pages with highest conversion rates</p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-purple-800 mb-2">Page Types</h4>
                            <p className="text-sm text-purple-700">Product detail vs. brand homepage analysis</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Copy Themes Section */}
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg border border-yellow-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-yellow-800">
                        <Palette className="h-5 w-5 text-yellow-600 mr-2" />
                        Copy & Messaging Analysis
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Identify which ad messages, hooks, and CTAs perform best for optimization.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-yellow-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2">Message Themes</h4>
                            <p className="text-sm text-yellow-700">Recurring high-engagement phrases</p>
                          </div>
                          <div className="bg-orange-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-orange-800 mb-2">CTA Performance</h4>
                            <p className="text-sm text-orange-700">Best performing call-to-action types</p>
                          </div>
                          <div className="bg-red-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-red-800 mb-2">Hook Analysis</h4>
                            <p className="text-sm text-red-700">Most effective attention-grabbing elements</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Scale vs Pause Section */}
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg border border-red-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-red-800">
                        <BarChart3 className="h-5 w-5 text-red-600 mr-2" />
                        Scale vs. Pause Decision Matrix
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Separate ads into two groups: high-performing for scaling and underperforming for pausing.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-green-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-green-800 mb-2">Scale Up</h4>
                            <p className="text-sm text-green-700">High ROAS, strong CTR, good conversion rates</p>
                          </div>
                          <div className="bg-red-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-red-800 mb-2">Pause/Optimize</h4>
                            <p className="text-sm text-red-700">Underperforming, draining budget, low efficiency</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Funnel Analysis Section */}
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-lg border border-teal-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-teal-800">
                        <BarChart3 className="h-5 w-5 text-teal-600 mr-2" />
                        Funnel Performance Analysis
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Track drop-offs across stages: impressions → clicks → add-to-cart → conversions.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="bg-teal-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-teal-800 mb-2">Impressions</h4>
                            <p className="text-sm text-teal-700">Reach and visibility metrics</p>
                          </div>
                          <div className="bg-cyan-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-cyan-800 mb-2">Clicks</h4>
                            <p className="text-sm text-cyan-700">Engagement and interest</p>
                          </div>
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">Add-to-Cart</h4>
                            <p className="text-sm text-blue-700">Intent and consideration</p>
                          </div>
                          <div className="bg-indigo-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-indigo-800 mb-2">Conversions</h4>
                            <p className="text-sm text-indigo-700">Final purchase actions</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ad Format Comparison Section */}
                    <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-6 rounded-lg border border-violet-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-violet-800">
                        <Play className="h-5 w-5 text-violet-600 mr-2" />
                        Ad Format Performance Comparison
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Compare video vs. static vs. carousel performance for different objectives.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-violet-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-violet-800 mb-2">Video</h4>
                            <p className="text-sm text-violet-700">Storytelling and demonstration</p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-purple-800 mb-2">Static</h4>
                            <p className="text-sm text-purple-700">Quick messaging and branding</p>
                          </div>
                          <div className="bg-pink-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-pink-800 mb-2">Carousel</h4>
                            <p className="text-sm text-pink-700">Multiple product showcase</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Creator Performance Section */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-6 rounded-lg border border-amber-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-amber-800">
                        <Sparkles className="h-5 w-5 text-amber-600 mr-2" />
                        Creator & Influencer Performance
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Rank creators by efficiency and identify where to double down or rethink briefs.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-amber-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-amber-800 mb-2">Top Performers</h4>
                            <p className="text-sm text-amber-700">Creators with highest engagement rates</p>
                          </div>
                          <div className="bg-yellow-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2">Content Style</h4>
                            <p className="text-sm text-yellow-700">Analyzing what resonates most</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Product Performance Section */}
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-6 rounded-lg border border-emerald-200">
                      <h3 className="text-lg font-semibold mb-4 flex items-center text-emerald-800">
                        <BarChart3 className="h-5 w-5 text-emerald-600 mr-2" />
                        Product & SKU Performance
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-gray-700 mb-4">
                          Show which products benefit most from paid creative and tie effectiveness to sales.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-emerald-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-emerald-800 mb-2">Top Products</h4>
                            <p className="text-sm text-emerald-700">Products with highest creative ROI</p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg">
                            <h4 className="font-semibold text-green-800 mb-2">Creative Impact</h4>
                            <p className="text-sm text-green-700">Direct correlation to product sales</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </>
      )}

      {/* Share Report Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Share2 className="h-5 w-5 text-green-600 mr-2" />
                Report Shared Successfully!
              </h3>
              <button
                onClick={() => setShareModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share Link (copied to clipboard)
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 text-sm bg-gray-50"
                  />
                  <button
                    onClick={() => navigator.clipboard.writeText(shareUrl)}
                    className="bg-green-600 text-white px-3 py-2 rounded-r-md hover:bg-green-700 transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Link is permanent:</strong> This report will never expire
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Anyone with this link can view your report without logging in.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShareModalOpen(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success('Link copied again!');
                  }}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Copy Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Media Modal */}
      {fullscreenMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full h-full flex items-center justify-center overflow-auto">
            <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
              {fullscreenMedia.type === 'video' ? (
                <video
                  src={fullscreenMedia.url}
                  controls
                  autoPlay
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                  onError={(e) => {
                    console.error('Video failed to load in modal:', fullscreenMedia.url);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <img
                  src={fullscreenMedia.url}
                  alt={fullscreenMedia.title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                  onError={(e) => {
                    console.error('Image failed to load in modal:', fullscreenMedia.url);
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <button
                onClick={() => setFullscreenMedia(null)}
                className="absolute top-4 right-4 p-3 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all z-10"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1e293b',
            color: '#f8fafc',
            border: '1px solid #475569',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#f8fafc',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f8fafc',
            },
          },
        }}
      />
    </div>
  );
};

export default CreativeReport;
